import React, { createContext, useContext, useState, useCallback } from 'react';
import { FaCheck, FaTimes, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue>({} as ToastContextValue);

const COLORS: Record<ToastType, { bg: string; icon: React.ReactNode; accent: string }> = {
  success: { bg: '#15803d', accent: '#166534', icon: <FaCheck /> },
  error: { bg: '#b91c1c', accent: '#991b1b', icon: <FaTimes /> },
  warning: { bg: '#b45309', accent: '#92400e', icon: <FaExclamationTriangle /> },
  info: { bg: '#1d4ed8', accent: '#1e40af', icon: <FaInfoCircle /> }
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info') => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, type, message }]);
      setTimeout(() => remove(id), type === 'error' || type === 'warning' ? 5000 : 3000);
    },
    [remove]
  );

  const value: ToastContextValue = {
    showToast,
    success: (m) => showToast(m, 'success'),
    error: (m) => showToast(m, 'error'),
    warning: (m) => showToast(m, 'warning'),
    info: (m) => showToast(m, 'info')
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
          zIndex: 30001, display: 'flex', flexDirection: 'column', gap: 8,
          maxWidth: '92vw', pointerEvents: 'none'
        }}
      >
        {toasts.map((t) => {
          const c = COLORS[t.type];
          return (
            <div
              key={t.id}
              onClick={() => remove(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: c.bg, color: '#fff',
                padding: '12px 18px', borderRadius: 10,
                boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
                fontSize: 16, fontWeight: 600,
                borderLeft: `4px solid ${c.accent}`,
                minWidth: 260, maxWidth: 460,
                pointerEvents: 'auto', cursor: 'pointer', wordBreak: 'break-word'
              }}
            >
              <span style={{ fontSize: 18, flexShrink: 0, display: 'flex' }}>{c.icon}</span>
              <span style={{ flex: 1 }}>{t.message}</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
